import React, { useEffect, useCallback, useState, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  ChevronDown,
  Clapperboard,
  type LucideIcon,
  Smartphone,
  Nfc,
  UserSearch,
  Database,
  UserCog,
  ShieldCheck,
  Users,
  Repeat,
  Percent,
  Tag,
  Layers,
  LineChart,
  Calculator,
  Building2,
  Building,
  Palette,
  Store,
  ShoppingCart,
  Image as ImageIcon,
  Home,
  CircleHelp,
} from "lucide-react"

/**
 * Mapa ri-* (que devuelve resolveMenuIcon) -> componente lucide. La migración
 * vive en la capa de render: el dato (item.icon) sigue siendo el string ri-*,
 * y MenuIcon dibuja el lucide equivalente; si un ri-* no está mapeado, cae al
 * <i> de fuente (no rompe). Íconos vectoriales nítidos y consistentes.
 */
const RI_TO_LUCIDE: Record<string, LucideIcon> = {
  "ri-sim-card-2-line": Nfc,
  "ri-file-user-line": UserSearch,
  "ri-database-2-line": Database,
  "ri-user-3-line": UserCog,
  "ri-shield-user-line": ShieldCheck,
  "ri-contacts-line": Users,
  "ri-smartphone-line": Smartphone,
  "ri-repeat-2-line": Repeat,
  "ri-coupon-line": Percent,
  "ri-price-tag-3-line": Tag,
  "ri-stack-line": Layers,
  "ri-movie-2-line": Clapperboard,
  "ri-line-chart-line": LineChart,
  "ri-calculator-line": Calculator,
  "ri-community-line": Building2,
  "ri-building-2-line": Building,
  "ri-bookmark-3-line": Palette,
  "ri-team-line": Users,
  "ri-store-2-line": Store,
  "ri-shopping-cart-2-line": ShoppingCart,
  "ri-image-line": ImageIcon,
  "ri-home-4-line": Home,
  "ri-question-line": CircleHelp,
}

function MenuIcon({ icon }: { icon: string }) {
  const Lucide = RI_TO_LUCIDE[icon]
  if (Lucide) return <Lucide className="menu-icon-lucide" aria-hidden="true" />
  return <i className={icon} />
}
import WithRouter, { type RouterProps } from "@/components/common/WithRouter"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import useMenuItems, { pathMatches, filterMenuItems } from "@/hooks/useMenuItems"
import { routePrefetchers } from "@/routes/allRoutes"
import type { RootState } from "@/store"
import { LayoutTypes } from "@/enums/layout"
import { tenantScopedLocalKey } from "@/utils/tenantStorage"

const OPEN_MENUS_STORAGE_KEY = "sidebar-open-menus"

/**
 * Prefetch on-hover (y on-focus, para teclado): calienta el chunk de la
 * página destino antes del click. Solo las rutas presentes en
 * routePrefetchers (5 páginas principales); el resto son no-op.
 * El import() de un chunk ya cargado resuelve al instante — no hace falta
 * memoizar qué ya se prefetcheó.
 */
function prefetchRouteChunk(link: string | undefined) {
  if (!link || link === "#" || link === "/#") return
  const key = link.split(/[?#]/)[0].replace(/\/+$/, "") || "/"
  routePrefetchers[key]?.().catch(() => {
    // fallo de red en el prefetch: silencioso, el lazy de la ruta reintenta
  })
}

function readPersistedOpenMenus(): Set<string> {
  try {
    const raw = localStorage.getItem(tenantScopedLocalKey(OPEN_MENUS_STORAGE_KEY))
    const parsed = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [])
  } catch {
    return new Set()
  }
}

interface VerticalLayoutProps {
  layoutType?: LayoutTypes
  router?: RouterProps
  /** Texto del filtro del sidebar (MenuFilter); vacío = sin filtrar */
  filterText?: string
}

const VerticalLayout: React.FC<VerticalLayoutProps> = (props) => {
  const navData = useMenuItems()
  const { pathname } = useLocation()

  const filterText = props.filterText ?? ""
  const isFiltering = filterText.trim().length > 0
  const visibleNavData = useMemo(
    () => filterMenuItems(navData, filterText),
    [navData, filterText]
  )

  // ESTADO LOCAL del sidebar — un Set con los menús abiertos. Single source
  // of truth, sin store externo, sin sincronización entre instancias, sin
  // refs, sin closures stale. React maneja todo.
  // Init lazy desde localStorage (tenant-scoped) para que los menús abiertos
  // sobrevivan recargas y sesiones. Ids obsoletos en storage son inofensivos:
  // el Set solo se consulta contra ids existentes del menú.
  const [openMenus, setOpenMenus] = useState<Set<string>>(readPersistedOpenMenus)

  useEffect(() => {
    try {
      localStorage.setItem(
        tenantScopedLocalKey(OPEN_MENUS_STORAGE_KEY),
        JSON.stringify([...openMenus])
      )
    } catch {
      // storage lleno o deshabilitado: la persistencia es best-effort
    }
  }, [openMenus])

  // En móvil (<767px) el sidebar es un drawer overlay: cerrarlo al navegar,
  // si no, queda tapando el contenido tras elegir una opción.
  useEffect(() => {
    if (document.documentElement.clientWidth <= 767) {
      document.body.classList.remove("vertical-sidebar-enable")
    }
  }, [pathname])

  // Cuando cambia la ruta, asegurarnos de que el padre del item activo esté
  // abierto. Solo agrega; no cierra los demás (el usuario los maneja).
  useEffect(() => {
    setOpenMenus((prev) => {
      const next = new Set(prev)
      let changed = false
      navData.forEach((item) => {
        if (item.hasSubItems) {
          const matches = item.subItems.some((sub) => pathMatches(sub.link, pathname))
          if (matches && !next.has(item.id)) {
            next.add(item.id)
            changed = true
          }
        }
      })
      return changed ? next : prev
    })
  }, [pathname, navData])

  const toggleMenu = useCallback((id: string) => {
    setOpenMenus((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectLayoutState = (state: RootState) => state.Layout
  const selectLayoutProperties = useMemo(
    () =>
      createSelector(selectLayoutState, (layout) => ({
        leftsidbarSizeType: layout.leftsidbarSizeType,
        sidebarVisibilitytype: layout.sidebarVisibilitytype,
        layoutType: layout.layoutType,
      })),
    []
  )

  const { leftsidbarSizeType, sidebarVisibilitytype, layoutType } = useSelector(
    selectLayoutProperties
  )

  // Handle resizing of the sidebar menu
  const resizeSidebarMenu = useCallback(() => {
    var windowSize = document.documentElement.clientWidth
    if (windowSize >= 1025) {
      if (document.documentElement.getAttribute("data-layout") === "vertical") {
        document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType)
      }
      if (document.documentElement.getAttribute("data-layout") === "semibox") {
        document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType)
      }
      var hamburgerIcon = document.querySelector(".hamburger-icon")
      if (
        (sidebarVisibilitytype === "show" ||
          layoutType === "vertical" ||
          layoutType === "twocolumn") &&
        hamburgerIcon
      ) {
        hamburgerIcon.classList.remove("open")
      } else if (hamburgerIcon) {
        hamburgerIcon.classList.add("open")
      }
    } else if (windowSize < 1025 && windowSize > 767) {
      document.body.classList.remove("twocolumn-panel")
      if (document.documentElement.getAttribute("data-layout") === "vertical") {
        document.documentElement.setAttribute("data-sidebar-size", "sm")
      }
      if (document.documentElement.getAttribute("data-layout") === "semibox") {
        document.documentElement.setAttribute("data-sidebar-size", "sm")
      }
      if (document.querySelector(".hamburger-icon")) {
        document.querySelector(".hamburger-icon")!.classList.add("open")
      }
    } else if (windowSize <= 767) {
      document.body.classList.remove("vertical-sidebar-enable")
      if (document.documentElement.getAttribute("data-layout") !== "horizontal") {
        document.documentElement.setAttribute("data-sidebar-size", "lg")
      }
      if (document.querySelector(".hamburger-icon")) {
        document.querySelector(".hamburger-icon")?.classList.add("open")
      }
    }
  }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType])

  useEffect(() => {
    window.addEventListener("resize", resizeSidebarMenu, true)
    return () => window.removeEventListener("resize", resizeSidebarMenu, true)
  }, [resizeSidebarMenu])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [props.router?.location.pathname])

  if (isFiltering && visibleNavData.length === 0) {
    return (
      <li className="nav-item">
        <span className="nav-link menu-link text-muted" aria-live="polite">
          Sin resultados
        </span>
      </li>
    )
  }

  return (
    <React.Fragment>
      {(visibleNavData || []).map((item) => {
        if (item.isHeader) {
          return (
            <li key={item.id} className="menu-title" title={item.label}>
              {/* En modo sm (icon-only) el CSS oculta el span y muestra el icono */}
              <i className="ri-more-fill"></i> <span>{item.label}</span>
            </li>
          )
        }

        if (item.hasSubItems) {
          // Mientras se filtra, expandir para mostrar los subitems coincidentes
          const isOpen = isFiltering || openMenus.has(item.id)
          const submenuId = `submenu-${item.id}`
          return (
            <li key={item.id} className="nav-item">
              {/* <button> real (no <a href="#">): Space también lo activa
                  y los lectores de pantalla lo anuncian como control, no enlace */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  const willOpen = !openMenus.has(item.id)
                  toggleMenu(item.id)
                  // Al abrir, traer el submenú a vista (los últimos items del
                  // sidebar abrían fuera del área visible)
                  if (willOpen) {
                    setTimeout(() => {
                      document
                        .getElementById(submenuId)
                        ?.scrollIntoView({ block: "nearest", behavior: "smooth" })
                    }, 80)
                  }
                }}
                className={`nav-link menu-link w-100 text-start border-0 bg-transparent${isOpen ? " active" : ""}`}
                aria-expanded={isOpen}
                aria-controls={submenuId}
                title={item.label}
                style={{ cursor: "pointer" }}
              >
                <MenuIcon icon={item.icon} /> <span>{item.label}</span>
                {/* Affordance: este item despliega un submenú */}
                <ChevronDown
                  aria-hidden="true"
                  className={`menu-chevron ms-auto h-4 w-4 shrink-0 transition-transform duration-200${isOpen ? " rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div id={submenuId} className="menu-dropdown show" style={{ display: "block" }}>
                  <ul className="nav nav-sm flex-column">
                    {item.subItems.map((subItem) => {
                      const isSubActive = pathMatches(subItem.link, pathname)
                      return (
                        <li key={subItem.id} className="nav-item">
                          <Link
                            to={subItem.link || "/#"}
                            className={`nav-link${isSubActive ? " active" : ""}`}
                            aria-current={isSubActive ? "page" : undefined}
                            title={subItem.label}
                            onMouseEnter={() => prefetchRouteChunk(subItem.link)}
                            onFocus={() => prefetchRouteChunk(subItem.link)}
                          >
                            <MenuIcon icon={subItem.icon || "ri-question-line"} /> <span>{subItem.label}</span>
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </li>
          )
        }

        const isActive = pathMatches(item.link, pathname)
        return (
          <li key={item.id} className="nav-item">
            <Link
              className={`nav-link menu-link${isActive ? " active" : ""}`}
              to={item.link || "/#"}
              aria-current={isActive ? "page" : undefined}
              title={item.label}
              onMouseEnter={() => prefetchRouteChunk(item.link)}
              onFocus={() => prefetchRouteChunk(item.link)}
            >
              <MenuIcon icon={item.icon} /> <span>{item.label}</span>
            </Link>
          </li>
        )
      })}
    </React.Fragment>
  )
}

export default WithRouter(VerticalLayout)
